import React, { useState, useEffect, useContext } from 'react';
import { 
  FaQuestionCircle, 
  FaCode, 
  FaCopy, 
  FaCheck,
  FaExternalLinkAlt,
  FaCog,
  FaChartLine,
  FaShieldAlt,
  FaPalette,
  FaMobile,
  FaDesktop,
  FaGlobe,
  FaRocket,
  FaBook,
  FaVideo,
  FaDownload,
  FaLightbulb,
  FaTools,
  FaUsers,
  FaEye,
  FaSync,
  FaTablet,
  FaKey,
  FaPlus,
  FaTrash,
  FaEyeSlash,
  FaUniversity
} from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { saasApiAxios } from '../../lib/axios-interceptor';

const PhdConnectHelpContent = () => {
  const { currentUser } = useContext(AuthContext);
  const [activeTab, setActiveTab] = useState('overview');
  const [copiedCode, setCopiedCode] = useState('');
  const [apiKeys, setApiKeys] = useState([]);
  const [universityId, setUniversityId] = useState('');
  const [showNewKey, setShowNewKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');
  const [loading, setLoading] = useState(false);
  const [universityLoading, setUniversityLoading] = useState(true);

  // Fetch university ID and API keys on component mount
  useEffect(() => {
    fetchUniversityInfo();
    fetchApiKeys();
  }, []);

  const fetchUniversityInfo = async () => {
    try {
      setUniversityLoading(true);
      const response = await saasApiAxios.get('/api/customer/me');
      if (response.data && response.data.id) {
        setUniversityId(response.data.id);
      }
    } catch (error) {
      console.error('Error fetching university info:', error);
    } finally {
      setUniversityLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    try {
      setLoading(true);
      const response = await saasApiAxios.get('/api/phd-connect/api-keys');
      setApiKeys(response.data || []);
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateApiKey = async () => {
    try {
      setLoading(true);
      const response = await saasApiAxios.post('/api/phd-connect/api-keys');
      const newKey = response.data.api_key;
      setNewApiKey(newKey);
      setShowNewKey(true);
      await fetchApiKeys(); // Refresh the list
    } catch (error) {
      console.error('Error generating API key:', error);
      alert('Failed to generate API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const deleteApiKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }
    
    try {
      setLoading(true);
      await saasApiAxios.delete(`/api/phd-connect/api-keys/${keyId}`);
      await fetchApiKeys(); // Refresh the list
    } catch (error) {
      console.error('Error deleting API key:', error);
      alert('Failed to delete API key. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(type);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const firstApiKey = apiKeys.length > 0 ? apiKeys[0].api_key : 'YOUR_API_KEY';
  const embedCode = `<div class="bg-white rounded-lg shadow-lg overflow-hidden">
  <div class="p-6 border-b bg-gray-50">
    <h3 class="text-xl font-semibold text-gray-900">Explore Our Latest PhD Opportunities</h3>
    <p class="text-gray-600 mt-1">
      Powered by <a href="https://phdscanner.com" target="_blank" rel="noopener noreferrer" class="font-semibold text-blue-600 hover:underline">PhDScanner.com</a>
    </p>
  </div>
  <div class="relative">
    <iframe 
      src="https://phdscanner.com/embed/opportunities?university_id=${universityId || 'YOUR_UNIVERSITY_ID'}&api_key=${firstApiKey}&theme=default&show_filters=true&items_per_page=10"
      width="100%" 
      height="800"
      title="PhD Opportunities"
      loading="lazy"
      class="border-0"
      sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
    ></iframe>
  </div>
</div>`;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FaBook },
    { id: 'api-keys', label: 'API Keys', icon: FaKey },
    { id: 'setup', label: 'Setup Guide', icon: FaTools },
    { id: 'integration', label: 'Integration', icon: FaCode },
    { id: 'customization', label: 'Customization', icon: FaPalette },
    { id: 'faq', label: 'FAQ', icon: FaQuestionCircle }
  ];

  return (
    <div className="w-full">
      {/* Header */}
      

      {/* Content */}
      <div className="flex flex-col md:flex-row h-auto md:h-[calc(100vh-300px)] min-h-0">
        {/* Sidebar (horizontal scroll on mobile) */}
        <div className="w-full md:w-64 bg-gray-50 border-r border-gray-200 p-2 md:p-4 rounded-xl md:mr-6 flex md:block overflow-x-auto md:overflow-visible space-x-2 md:space-x-0 md:space-y-2 mb-4 md:mb-0">
          <nav className="flex md:block w-full">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 md:w-full flex items-center gap-2 md:gap-3 px-2 md:px-4 py-2 md:py-3 rounded-lg text-left transition-colors whitespace-nowrap text-xs md:text-base ${
                    activeTab === tab.id
                      ? 'bg-[#DB4F40] text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon className="text-base md:text-lg" />
                  <span className="font-bold text-lg md:text-xl">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto p-2 md:p-6 min-h-0">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">What is PhD Connect?</h2>
                <p className="text-gray-600 leading-relaxed">
                  PhD Connect is a powerful integration tool that allows universities to display their PhD opportunities 
                  directly on their own websites. Think of it as a "live feed" that automatically syncs with your 
                  PhDScanner opportunities and displays them with your university's branding.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                  <div className="flex items-center gap-3 mb-4">
                    <FaGlobe className="text-2xl text-blue-600" />
                    <h3 className="text-lg font-semibold text-blue-800">Website Integration</h3>
                  </div>
                  <p className="text-blue-700">
                    Embed opportunities on your university website, research pages, or graduate school portal 
                    with just a few lines of code.
                  </p>
                </div>

                <div className="bg-green-50 p-6 rounded-xl border border-green-200">
                  <div className="flex items-center gap-3 mb-4">
                    <FaSync className="text-2xl text-green-600" />
                    <h3 className="text-lg font-semibold text-green-800">Real-time Sync</h3>
                  </div>
                  <p className="text-green-700">
                    Opportunities automatically update when you add, edit, or remove them from your PhDScanner dashboard.
                  </p>
                </div>

                <div className="bg-purple-50 p-6 rounded-xl border border-purple-200">
                  <div className="flex items-center gap-3 mb-4">
                    <FaPalette className="text-2xl text-purple-600" />
                    <h3 className="text-lg font-semibold text-purple-800">Customizable Design</h3>
                  </div>
                  <p className="text-purple-700">
                    Choose from multiple themes and customize colors, layout, and functionality to match your brand.
                  </p>
                </div>
              </div>

              <div className="bg-gradient-to-r from-[#FFEEED] to-[#FFD6D4] p-6 rounded-xl">
                <h3 className="text-xl font-bold text-[#1B222A] mb-4 flex items-center gap-3">
                  <FaLightbulb className="text-[#DB4F40]" />
                  Why Use PhD Connect?
                </h3>
                <ul className="space-y-3 text-[#1B222A]">
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-[#DB4F40] mt-1 flex-shrink-0" />
                    <span><strong>No Double Entry:</strong> Upload once to PhDScanner, display everywhere</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-[#DB4F40] mt-1 flex-shrink-0" />
                    <span><strong>Professional Appearance:</strong> Maintain your university's branding and design</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-[#DB4F40] mt-1 flex-shrink-0" />
                    <span><strong>Increased Visibility:</strong> Reach more students through your own website</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <FaCheck className="text-[#DB4F40] mt-1 flex-shrink-0" />
                    <span><strong>Easy Maintenance:</strong> Updates happen automatically, no manual work required</span>
                  </li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'api-keys' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">API Key Management</h2>
                <p className="text-gray-600 mb-6">
                  Generate and manage API keys for embedding PhD opportunities on your website.
                </p>
              </div>

              {/* University ID Section */}
              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center gap-2">
                  <FaUniversity className="text-blue-600" />
                  Your University ID
                </h3>
                <p className="text-blue-700 mb-4">
                  This is your unique university identifier. Use this in your embed codes.
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 bg-white p-3 rounded-lg border border-blue-300">
                    {universityLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-blue-800">Loading...</span>
                      </div>
                    ) : universityId ? (
                      <code className="text-blue-800 font-mono">{universityId}</code>
                    ) : (
                      <span className="text-red-600">Failed to load university ID</span>
                    )}
                  </div>
                  <button
                    onClick={() => copyToClipboard(universityId, 'university-id')}
                    className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    disabled={!universityId || universityLoading}
                  >
                    {copiedCode === 'university-id' ? <FaCheck /> : <FaCopy />}
                    {copiedCode === 'university-id' ? 'Copied!' : 'Copy'}
                  </button>
                </div>
              </div>

              {/* API Keys Section */}
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800">API Keys</h3>
                  <button
                    onClick={generateApiKey}
                    disabled={loading}
                    className="flex items-center gap-2 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors disabled:opacity-50"
                  >
                    <FaPlus className="w-4 h-4" />
                    Generate New Key
                  </button>
                </div>

                {loading && (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DB4F40]"></div>
                  </div>
                )}

                {!loading && apiKeys.length === 0 && (
                  <div className="bg-gray-50 p-8 rounded-xl text-center">
                    <FaKey className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-gray-600 mb-2">No API Keys Found</h4>
                    <p className="text-gray-500 mb-4">Generate your first API key to start embedding opportunities.</p>
                    <button
                      onClick={generateApiKey}
                      className="flex items-center gap-2 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors mx-auto"
                    >
                      <FaPlus className="w-4 h-4" />
                      Generate API Key
                    </button>
                  </div>
                )}

                {!loading && apiKeys.length > 0 && (
                  <div className="space-y-4">
                    {apiKeys.map((key) => (
                      <div key={key.id} className="bg-white border border-gray-200 rounded-xl p-6">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-800">{key.name || 'API Key'}</h4>
                            <p className="text-sm text-gray-500">
                              Created: {new Date(key.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2 w-full md:w-auto">
                            <button
                              onClick={() => copyToClipboard(key.api_key, `key-${key.id}`)}
                              className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm w-full md:w-auto"
                            >
                              {copiedCode === `key-${key.id}` ? <FaCheck /> : <FaCopy />}
                              {copiedCode === `key-${key.id}` ? 'Copied!' : 'Copy'}
                            </button>
                            <button
                              onClick={() => deleteApiKey(key.id)}
                              className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm w-full md:w-auto"
                            >
                              <FaTrash className="w-4 h-4" />
                              Delete
                            </button>
                          </div>
                        </div>
                        <div className="bg-gray-100 p-3 rounded-lg">
                          <code className="text-gray-800 font-mono text-sm break-all">{key.api_key}</code>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Newly Generated Key Modal */}
                {showNewKey && newApiKey && (
                  <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full p-8">
                      <div className="text-center mb-6">
                        <FaKey className="w-16 h-16 text-green-600 mx-auto mb-4" />
                        <h3 className="text-2xl font-bold text-gray-800 mb-2">API Key Generated!</h3>
                        <p className="text-gray-600">
                          Your new API key has been created successfully. Copy it now - you won't be able to see it again!
                        </p>
                      </div>
                      
                      <div className="bg-gray-100 p-4 rounded-lg mb-6">
                        <code className="text-gray-800 font-mono text-sm break-all">{newApiKey}</code>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-4 justify-center w-full">
                        <button
                          onClick={() => copyToClipboard(newApiKey, 'new-key')}
                          className="flex items-center gap-2 px-6 py-3 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors w-full sm:w-auto"
                        >
                          {copiedCode === 'new-key' ? <FaCheck /> : <FaCopy />}
                          {copiedCode === 'new-key' ? 'Copied!' : 'Copy API Key'}
                        </button>
                        <button
                          onClick={() => {
                            setShowNewKey(false);
                            setNewApiKey('');
                          }}
                          className="px-6 py-3 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors w-full sm:w-auto"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Security Tips */}
              <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200">
                <h4 className="font-semibold text-yellow-800 mb-2 flex items-center gap-2">
                  <FaShieldAlt className="text-yellow-600" />
                  Security Tips
                </h4>
                <ul className="text-sm text-yellow-700 space-y-1">
                  <li>• Keep your API keys secure and don't share them publicly</li>
                  <li>• Regenerate keys if you suspect they've been compromised</li>
                  <li>• Use different keys for different websites if needed</li>
                  <li>• Monitor your API usage for any unusual activity</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'setup' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Setup Guide</h2>
                <p className="text-gray-600 mb-6">
                  Follow these steps to get PhD Connect working on your website in minutes.
                </p>
              </div>

              <div className="space-y-6">
                <div className="border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#DB4F40] text-white rounded-full flex items-center justify-center font-bold">1</div>
                    <h3 className="text-lg font-semibold">Generate API Key</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    First, you need to create an API key that will securely connect your website to your PhDScanner opportunities.
                  </p>
                  <div className="bg-gray-100 p-4 rounded-lg">
                    <p className="text-sm text-gray-700">
                      <strong>Location:</strong> PhD Connect → API Keys
                    </p>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#DB4F40] text-white rounded-full flex items-center justify-center font-bold">2</div>
                    <h3 className="text-lg font-semibold">Choose Integration Method</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    We provide a simple iFrame integration that works with any website. Just copy and paste the code snippet.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-800 mb-2">iFrame (Easiest)</h4>
                    <p className="text-sm text-blue-700">Copy-paste HTML code. Works with any website.</p>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#DB4F40] text-white rounded-full flex items-center justify-center font-bold">3</div>
                    <h3 className="text-lg font-semibold">Add to Your Website</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Integrate the code into your website where you want the opportunities to appear.
                  </p>
                  <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                    <h4 className="font-semibold text-yellow-800 mb-2">💡 Pro Tips:</h4>
                    <ul className="text-sm text-yellow-700 space-y-1">
                      <li>• Place on your graduate school or research opportunities page</li>
                      <li>• Consider adding to department-specific pages</li>
                      <li>• Test on mobile devices to ensure responsiveness</li>
                      <li>• Monitor analytics to optimize placement</li>
                    </ul>
                  </div>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-[#DB4F40] text-white rounded-full flex items-center justify-center font-bold">4</div>
                    <h3 className="text-lg font-semibold">Test & Customize</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    Verify everything is working and customize the appearance to match your brand.
                  </p>
                  <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                    <h4 className="font-semibold text-green-800 mb-2">✅ Checklist:</h4>
                    <ul className="text-sm text-green-700 space-y-1">
                      <li>• Opportunities are displaying correctly</li>
                      <li>• Search and filters are working</li>
                      <li>• Links open in new tabs</li>
                      <li>• Design matches your brand</li>
                      <li>• Mobile view looks good</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'integration' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Integration Methods</h2>
                <p className="text-gray-600 mb-6">
                  We offer a simple and universal iFrame integration. Below you'll find the code to embed on your website, which will be automatically populated with your University ID and API Key.
                </p>
              </div>

              <div className="space-y-8">
                {/* iFrame Method */}
                <div className="border border-gray-200 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <FaGlobe className="text-2xl text-[#DB4F40]" />
                    <h3 className="text-xl font-semibold">iFrame Integration (Recommended for most users)</h3>
                  </div>
                  <p className="text-gray-600 mb-4">
                    The simplest way to add PhD opportunities to any website. Just copy and paste the HTML code.
                  </p>
                  
                  <div className="bg-gray-900 text-green-400 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                    <pre>{embedCode}</pre>
                  </div>
                  
                  <button
                    onClick={() => copyToClipboard(embedCode, 'iframe')}
                    className="mt-4 flex items-center gap-2 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors"
                  >
                    {copiedCode === 'iframe' ? <FaCheck /> : <FaCopy />}
                    {copiedCode === 'iframe' ? 'Copied!' : 'Copy Code'}
                  </button>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-blue-800 mb-2">✅ Pros:</h4>
                      <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Works with any website</li>
                        <li>• No coding required</li>
                        <li>• Easy to implement</li>
                        <li>• Automatic updates</li>
                      </ul>
                    </div>

                  </div>
                </div>

                {/* React Method */}
                
              </div>
            </div>
          )}

          {activeTab === 'customization' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Customization Options</h2>
                <p className="text-gray-600 mb-6">
                  Make PhD Connect look and feel like it belongs on your website.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Themes */}
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FaPalette className="text-[#DB4F40]" />
                    Theme Options
                  </h3>
                  <ul className="list-disc pl-6 text-gray-800 text-base">
                    <li>Default (Blue)</li>
                    <li>Blue</li>
                    <li>Green</li>
                    <li>Orange</li>
                    <li>Purple</li>
                    <li>Teal</li>
                    <li>Pink</li>
                    <li>Black</li>
                    <li>Maroon</li>
                    <li>Gold</li>
                    <li>Gray</li>
                  </ul>
                </div>

                {/* Layout Options */}
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FaDesktop className="text-[#DB4F40]" />
                    Layout Options
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span>Show Filters</span>
                      <div className="flex gap-2">
                        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">On</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">Off</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Items per Page</span>
                      <div className="flex gap-1">
                        <span className="px-2 py-1 bg-[#DB4F40] text-white rounded text-sm">5</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">10</span>
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-sm">20</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Pagination</span>
                      <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-sm">Automatic</span>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-gray-600">The default items per page is <strong>10</strong>.</p>
                </div>



                {/* Responsive Design */}
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FaMobile className="text-[#DB4F40]" />
                    Responsive Design
                  </h3>
                  <p className="text-gray-600 mb-4">
                    PhD Connect automatically adapts to different screen sizes and devices.
                  </p>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <FaDesktop className="text-gray-400" />
                      <span className="text-sm">Desktop: Full layout with all features</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaMobile className="text-gray-400" />
                      <span className="text-sm">Mobile: Optimized for touch interaction</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <FaTablet className="text-gray-400" />
                      <span className="text-sm">Tablet: Adaptive layout</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'faq' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-800 mb-4">Frequently Asked Questions</h2>
              </div>

              <div className="space-y-4">
                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">How much does PhD Connect cost?</h3>
                  <p className="text-gray-600">
                    PhD Connect is included with your PhDScanner subscription. There are no additional costs for embedding opportunities on your website.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">Do I need technical skills to use PhD Connect?</h3>
                  <p className="text-gray-600">
                    No! The iFrame integration requires no technical skills - just copy and paste the code. For React integration, basic React knowledge is helpful but not required.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">How often do opportunities update?</h3>
                  <p className="text-gray-600">
                    Opportunities update in real-time. When you add, edit, or remove opportunities in your PhDScanner dashboard, the changes appear on your website immediately.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">Can I customize the appearance?</h3>
                  <p className="text-gray-600">
                    Yes! You can choose from multiple themes, customize colors, and even add custom CSS for advanced styling to match your university's brand.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">Is PhD Connect mobile-friendly?</h3>
                  <p className="text-gray-600">
                    Absolutely! PhD Connect is fully responsive and optimized for all devices including smartphones, tablets, and desktop computers.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">What if I need help with integration?</h3>
                  <p className="text-gray-600">
                    We provide comprehensive documentation, video tutorials, and email support. Our team is here to help you get PhD Connect working perfectly on your website.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">Can I embed on multiple websites?</h3>
                  <p className="text-gray-600">
                    Yes! You can use the same API key to embed opportunities on multiple websites, such as your main university site, department pages, and research portals.
                  </p>
                </div>

                <div className="border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold mb-2">How secure is the API key?</h3>
                  <p className="text-gray-600">
                    API keys are encrypted and secure. They only provide read access to your opportunities and can be regenerated at any time if needed.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 p-6 rounded-xl border border-blue-200">
                <h3 className="text-lg font-semibold text-blue-800 mb-4">Still have questions?</h3>
                <p className="text-blue-700 mb-4">
                  Our support team is here to help you get the most out of PhD Connect.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 w-full">
                  <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors w-full sm:w-auto">
                    <FaVideo />
                    Watch Tutorial
                  </button>
                  <button className="flex items-center gap-2 px-4 py-2 bg-[#DB4F40] text-white rounded-lg hover:bg-[#c13426] transition-colors w-full sm:w-auto">
                    <FaDownload />
                    Download Guide
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PhdConnectHelpContent; 